/**
 * Reload the app onto the current build.
 *
 * A plain `location.reload()` is not enough. This app can be installed and
 * served by a service worker whose install-time precache INTERCEPTS fetches, so
 * a reload can be answered from a stale bundle without the network being
 * consulted at all — which is exactly the state someone is in when they reach
 * for a reload button because the app is behaving like an older version.
 *
 * So: drop the worker and Cache Storage, re-prime the HTTP cache for the shell,
 * then reload.
 *
 * IndexedDB IS NEVER TOUCHED. Every project, clip and note lives there; a
 * "reload" that cleared it would destroy the user's work to fix a caching
 * problem. Only the code caches go.
 *
 * Everything is best-effort behind a deadline: a reload must never be held
 * hostage by a slow network or by storage the browser has blocked (private
 * mode). The worst case is reloading without having cleared everything, which
 * is what would have happened anyway.
 */

/** How long to wait for the cache clear before reloading regardless. */
export const RELOAD_DEADLINE_MS = 4000

export interface ReloadOptions {
  /** Extra URLs to re-prime alongside the shell (e.g. a chunk that failed). */
  alsoPrime?: string[]
  /** Test seams. */
  reload?: () => void
  now?: () => Promise<void>
}

/** Drop the service worker and Cache Storage. Never IndexedDB. */
export async function clearCodeCaches(): Promise<void> {
  try {
    const registrations = await (navigator.serviceWorker?.getRegistrations?.() ?? [])
    await Promise.all(registrations.map((registration) => registration.unregister()))
  } catch {
    // Storage access blocked — the reload is still worth trying.
  }
  try {
    const keys = await (self.caches?.keys?.() ?? [])
    await Promise.all(keys.map((key) => caches.delete(key)))
  } catch {
    // As above.
  }
}

/**
 * Clear the code caches, re-prime the shell, and reload.
 *
 * Returns once the reload has been requested. Callers do not need to await it —
 * the page is going away.
 */
export async function reloadApp(options: ReloadOptions = {}): Promise<void> {
  const reload = options.reload ?? (() => location.reload())
  const recover = (async () => {
    await clearCodeCaches()
    await Promise.allSettled(
      ['/', ...(options.alsoPrime ?? [])].map((url) => fetch(url, { cache: 'reload' })),
    )
  })()
  const deadline = new Promise<void>((resolve) => {
    setTimeout(resolve, RELOAD_DEADLINE_MS)
  })
  await Promise.race([recover, deadline])
  reload()
}
